// range/bucket.ts - bucketRange(): classify every live combo into value / strongDraw / weakDraw /
// showdown / air / trap relative to the current board (ENGINE_DESIGN §4.4). Buckets feed Mode C
// visualization, the strategy engine's target analysis ("what worse calls / what better folds"), and
// the facts digest's rangeShape. `trap` = slowplayed monsters: nutted classes the archetype rarely
// raises (the Waiting Room's signature) - so trap detection needs the archetype; without one, nutted
// hands fall in `value`.
import { RANGE_BUCKETS, } from '@postflop/schema';
import { cardToString } from '../core/cards';
import { COMBO_CARDS, COMBO_COUNT } from './combos';
import { classifyCombo, classRank } from './classify';
import { raiseProbFor } from './action-model';
/** Nutted classes eligible to be flagged as a trap when the archetype rarely raises them. */
const NUTTED_MIN = classRank('set');
/** Below this raiseProb, a nutted class is a slowplay candidate (trap) rather than a raised value bet. */
const TRAP_RAISE_CEILING = 0.35;
/** Made-hand strength at/above which a hand is "value" (bet/raise for value). */
const VALUE_MIN = classRank('top_pair_good_kicker');
/** Base bucket for a class, before trap adjustment. */
const baseBucket = (hc) => {
    const r = classRank(hc);
    if (r >= VALUE_MIN)
        return 'value';
    switch (hc) {
        case 'strong_draw': return 'strongDraw';
        case 'weak_draw': return 'weakDraw';
        case 'air': return 'air';
        // marginal made hands + high-card showdown value = bluff-catcher region
        default: return 'showdown';
    }
};
/**
 * Bucket a range against the board. Pass the villain's archetype to enable trap detection (nutted
 * classes it slowplays); omit it and nutted hands stay in `value`. Combo mass is weighted (Σ weight),
 * so a 0.5-weight combo counts as half a combo. `top` lists up to 6 representative combos per bucket
 * (highest weight first) as [highCard, lowCard] string pairs.
 */
export const bucketRange = (range, board, arch) => {
    const combosByBucket = blankNumbers();
    const reps = {
        value: [], strongDraw: [], weakDraw: [], showdown: [], air: [], trap: [],
    };
    let total = 0;
    // A combo that uses a board card is impossible (the villain can't hold a card that's on the felt), so
    // it contributes nothing to the range shape. Skip it here rather than trust every caller to hand us a
    // board-clean range: classifying a board-colliding combo builds a 7-card hand with a duplicate card,
    // which crashes the evaluator ("C is not iterable" from @poker-apprentice describeHand) on some inputs
    // and returns a silently-wrong hand on others. This mirrors narrow()'s board-skip (range/narrow.ts) and
    // is a no-op for an already-clean range (no combo collides), so callers that pre-remove see no change.
    const boardSet = new Set(board.map((c) => c));
    for (let idx = 0; idx < COMBO_COUNT; idx++) {
        const w = range.weights[idx];
        if (w <= 0)
            continue;
        const [a, b] = COMBO_CARDS[idx];
        if (boardSet.has(a) || boardSet.has(b))
            continue;
        const { handClass } = classifyCombo([a, b], board);
        let bucket = baseBucket(handClass);
        if (arch && classRank(handClass) >= NUTTED_MIN && raiseProbFor(arch, handClass) < TRAP_RAISE_CEILING) {
            bucket = 'trap';
        }
        combosByBucket[bucket] += w;
        reps[bucket].push({ idx, w });
        total += w;
    }
    const buckets = {};
    for (const bucket of RANGE_BUCKETS) {
        const combos = combosByBucket[bucket];
        const top = reps[bucket]
            .sort((x, y) => y.w - x.w)
            .slice(0, 6)
            .map(({ idx }) => {
            const [a, b] = COMBO_CARDS[idx];
            return [cardToString(a), cardToString(b)];
        });
        buckets[bucket] = {
            combos: round2(combos),
            pctOfRange: total > 0 ? round2((combos / total) * 100) : 0,
            top,
        };
    }
    return { buckets };
};
const blankNumbers = () => ({
    value: 0, strongDraw: 0, weakDraw: 0, showdown: 0, air: 0, trap: 0,
});
const round2 = (x) => Math.round(x * 100) / 100;
//# sourceMappingURL=bucket.js.map